/**
 * Text document templates (HTML — TipTap-compatible).
 *
 * Each template is pasted into the editor via `editor.commands.setContent(html)`.
 * Built strictly against StarterKit + our current extensions:
 *   - Headings (h1/h2/h3), paragraphs, strong, em
 *   - Lists (ul/ol), blockquote, code
 *   - hr, links, highlight, images, text-align
 * No native tables — the source .docx tables are serialised as labelled
 * paragraphs and bullet lists so the layout stays editable under StarterKit
 * without extra dependencies.
 */

export interface TextDocTemplate {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly html: string;
}

// ---------------------------------------------------------------------------
// Template: Mémoire d'invention (one-pager)
// Ported from the client's generate_memoire_invention.js (docx generator).
// Colours match the Word template:
//   - Navy section headings (h2 colour + bottom border) : #1F3864
//   - Confidential notice                               : #C00000
//   - Placeholder dashes                                : #A6A6A6
//   - Footer disclaimer                                 : #808080
// ---------------------------------------------------------------------------

const NAVY_H2 = 'style="color:#1F3864;border-bottom:2px solid #1F3864;padding-bottom:2px"';
const DASHES = '<span style="color:#A6A6A6">_______________________________________________</span>';

const MEMOIRE_INVENTION_HTML = `
<h1 style="text-align:center;color:#1F3864;margin-bottom:4px">MÉMOIRE D'INVENTION</h1>
<p style="text-align:center;color:#C00000"><em>CONFIDENTIEL — Support d'arbitrage brevet / secret d'affaires</em></p>

<h2 ${NAVY_H2}>1. IDENTIFICATION</h2>
<p><strong>Titre de l'invention :</strong> ${DASHES}</p>
<p><strong>Réf. interne / Date :</strong> ${DASHES}</p>
<p><strong>Inventeur(s) + % contribution :</strong> ${DASHES}</p>
<p><strong>Entité propriétaire :</strong> ${DASHES}</p>
<p><strong>Cadre</strong> (mission inventive, contrat, collaboration) <strong>:</strong> ${DASHES}</p>
<p><strong>Date de conception :</strong> ${DASHES}</p>
<p><strong>Divulgation antérieure ?</strong> ☐ Non &nbsp;&nbsp; ☐ Sous NDA &nbsp;&nbsp; ☐ Publique (date : ___________)</p>

<h2 ${NAVY_H2}>2. DESCRIPTION TECHNIQUE</h2>
<p><strong>Problème technique résolu :</strong> ${DASHES}</p>
<p>${DASHES}</p>
<p><strong>État de l'art le plus proche</strong> (brevets, produits) <strong>+ limitations :</strong> ${DASHES}</p>
<p>${DASHES}</p>
<p><strong>Principe de l'invention</strong> (caractéristiques essentielles) <strong>:</strong> ${DASHES}</p>
<p>${DASHES}</p>
<p>${DASHES}</p>
<p><strong>Avantages mesurables vs art antérieur :</strong> ${DASHES}</p>
<p>${DASHES}</p>

<h2 ${NAVY_H2}>3. ARBITRAGE BREVET / SECRET</h2>
<ul>
  <li><strong>Détectable par rétro-ingénierie ?</strong> ☐ Oui → brevet &nbsp;&nbsp; ☐ Non → secret possible</li>
  <li><strong>Durée de vie économique :</strong> ☐ &lt; 20 ans &nbsp;&nbsp; ☐ &gt; 20 ans (secret avantageux)</li>
  <li><strong>Contournement facile ?</strong> ☐ Oui &nbsp;&nbsp; ☐ Non</li>
  <li><strong>Pays / marchés visés :</strong> ${DASHES}</li>
  <li><strong>Orientation retenue :</strong> ☐ Brevet &nbsp;&nbsp; ☐ Secret &nbsp;&nbsp; ☐ Mixte &nbsp;&nbsp; ☐ Publication défensive</li>
</ul>

<h2 ${NAVY_H2}>4. SI SECRET : MESURES DE PROTECTION RAISONNABLES (L.151-1 C. com.)</h2>
<ul>
  <li><strong>Organisationnelles :</strong> ☐ classification &nbsp; ☐ accès restreint &nbsp; ☐ formation collaborateurs</li>
  <li><strong>Contractuelles :</strong> ☐ clauses de confidentialité dans les contrats de travail &nbsp; ☐ NDA partenaires / sous-traitants</li>
  <li><strong>Techniques :</strong> ☐ contrôle d'accès &nbsp; ☐ chiffrement &nbsp; ☐ traçabilité &nbsp; ☐ cloisonnement</li>
  <li><strong>Horodatage</strong> (enveloppe Soleau, huissier, blockchain) : ______________________________</li>
</ul>

<h2 ${NAVY_H2}>5. SIGNATURES</h2>
<p><strong>Inventeur principal :</strong> ${DASHES}</p>
<p><strong>Responsable PI / Direction :</strong> ${DASHES}</p>
<p><strong>Date :</strong> ${DASHES}</p>

<hr>
<p style="color:#808080"><em><strong>Avertissement :</strong> support interne non juridique. Consulter un CPI ou avocat en PI avant tout dépôt.</em></p>
`.trim();

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const TEXTDOC_TEMPLATES: readonly TextDocTemplate[] = [
  {
    id: 'memoire-invention',
    label: "Mémoire d'invention",
    description: "Support d'arbitrage brevet / secret d'affaires",
    html: MEMOIRE_INVENTION_HTML,
  },
];
